/**
 * Забытый пароль: письмо со ссылкой и новый пароль по ней.
 *
 * Раньше у ошибки входа не было следующего шага: «Неверная почта или пароль»
 * — и всё. Теперь из входа можно попросить ссылку, а по ссылке задать новый
 * пароль и сразу оказаться в приложении.
 *
 * Ответ на просьбу одинаков для любой почты: экран не подтверждает и не
 * опровергает, что адрес зарегистрирован. Иначе форма сброса стала бы
 * справочником чужих аккаунтов.
 */
import { useEffect, useState } from 'react';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { PasswordField } from './password-field.js';
import { confirmPasswordReset, passwordResetStatus, requestPasswordReset, type User } from './sync.js';

/** «Забыли пароль?»: почта → письмо со ссылкой. */
export function ForgotPassword({ email: initial, onBack, onClose, context = 'login' }: {
  /** Почта, уже набранная во входе: вводить её второй раз незачем. */
  email: string;
  context?: 'login' | 'profile';
  onBack: () => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<'checking' | 'available' | 'unavailable' | 'offline'>(context === 'profile' ? 'checking' : 'available');
  const check = async () => { setStatus('checking'); setStatus(await passwordResetStatus()); };
  useEffect(() => {
    if (context !== 'profile') return;
    let active = true;
    void passwordResetStatus().then((s) => { if (active) setStatus(s); });
    return () => { active = false; };
  }, [context]);

  const send = async () => {
    setBusy(true); setErr(null);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : 'Не получилось отправить письмо. Проверь соединение и попробуй ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SheetWrap title={context === 'profile' ? 'Сменить пароль' : 'Забыли пароль?'} onClose={onClose} onBack={onBack} backLabel={context === 'profile' ? 'Назад в профиль' : 'Назад ко входу'}
      tip={sent
        ? 'Письма нет через несколько минут — загляни в «Спам» или попроси ссылку ещё раз.'
        : 'Пришлём на почту ссылку — по ней задашь новый пароль.'}>
      <div className="form">
        {sent ? (
          <>
            <div className="okline" role="status">
              Если почта {email.trim()} зарегистрирована, письмо со ссылкой уже в пути.
              Ссылка действует час.
            </div>
            <button className="btn" onClick={onBack}>{context === 'profile' ? 'В профиль' : 'Ко входу'}</button>
          </>
        ) : (
          <>
            <label>Почта
              <input value={email} onChange={(e) => setEmail(e.target.value)}
                type="email" inputMode="email" autoComplete="email" readOnly={context === 'profile'} autoFocus={context !== 'profile'} /></label>
            {status === 'checking' && <p role="status">Проверяю отправку письма…</p>}
            {(status === 'offline' || status === 'unavailable') && <>
              <div className="note warn" role="alert">{status === 'offline'
                ? 'Нет связи с сервером. Проверь соединение и попробуй ещё раз.'
                : 'Отправка писем сейчас недоступна. Попробуй позже.'}</div>
              <button className="btn" onClick={() => void check()}>Проверить ещё раз</button>
            </>}
            {err && <div className="note warn" role="alert">{err}</div>}
            <button className="btn pri" disabled={busy || !email.trim() || status !== 'available'} onClick={() => void send()}>
              {busy ? 'Отправляю…' : 'Прислать ссылку'}
            </button>
          </>
        )}
      </div>
    </SheetWrap>
  );
}

/**
 * Код из ссылки, а адрес — без него.
 *
 * Код убирается из адресной строки сразу: иначе он остаётся в истории
 * браузера и в закладке, сделанной по рассеянности.
 */
function takeToken(): string {
  if (typeof location === 'undefined') return '';
  const token = new URLSearchParams(location.search).get('token') ?? '';
  if (token && typeof history !== 'undefined') history.replaceState({}, '', '/reset');
  return token;
}

/** Новый пароль по ссылке из письма. */
export function ResetPassword({ onDone, onLogin }: {
  /** Пароль сменён, человек вошёл. */
  onDone: (user: User) => void | Promise<void>;
  onLogin: () => void;
}) {
  const [token] = useState(takeToken);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Ссылка не годится: кода нет, он устарел или уже сработал. */
  const [dead, setDead] = useState(token === '');

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const user = await confirmPasswordReset(token, password);
      await onDone(user);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Не получилось сменить пароль';
      setErr(message);
      if (/устарела|использована/i.test(message)) setDead(true);
      setBusy(false);
    }
  };

  return (
    <SheetWrap title="Смена пароля" onClose={onLogin}>
      <div className="form">
        {dead ? (
          <>
            <div className="note warn" role="alert" style={{ marginTop: 0 }}>
              {err ?? 'В ссылке нет кода. Открой ссылку из письма целиком или попроси новую во входе — «Забыли пароль?».'}
            </div>
            <button className="btn pri" onClick={onLogin}>Ко входу</button>
          </>
        ) : (
          <>
            <label>Новый пароль
              <Tip>На других устройствах аккаунт закроется — там войдёшь уже с новым паролем.</Tip>
              <PasswordField value={password} onChange={setPassword} autoComplete="new-password" autoFocus /></label>
            <div className="note">Не короче восьми знаков.</div>
            {err && <div className="note warn" role="alert">{err}</div>}
            <button className="btn pri" disabled={busy || password.length < 8} onClick={() => void save()}>
              {busy ? 'Сохраняю…' : 'Сохранить и войти'}
            </button>
          </>
        )}
      </div>
    </SheetWrap>
  );
}
